import React from 'react';
import Organizer from '../../../models/inkind-organizers/Organizer';

import Checkbox from '../../giveinkind/components/Checkbox';
import IconButton, { IconButtonStyle } from '../../giveinkind/components/IconButton';
import Button, { ButtonStyle } from '../../giveinkind/components/Button';
import ConfirmDialog from '@gik/ui/ConfirmDialog';
import InKindOrganizersAPI from '../../../api/inkind-organizers/InkindOrganizersAPI';
import OrganizerInvitation from '../../../models/inkind-organizers/OrganizerInvitation';
import InkindOrganizerSettings from '../../../models/inkinds/InkindOrganizerSettings';
import { getCDNAssetsUrl } from '@gik/core/utils/LinkUtils';
import { defaultUserImagePath } from '@gik/core/constants';

declare var window: LegacyReactWindow;

export interface IBaseOrganizerItemProps {
  isOwner: boolean;
  isInvitee: boolean;
  className?: string;
  organizer: Organizer;
  onUpdateSettings: () => void;
  onRemoveOrganizer: () => void;
  routeId: string;
}

export function BaseOrganizerItem({
  isOwner,
  isInvitee,
  className = '',
  organizer,
  onUpdateSettings,
  onRemoveOrganizer,
  routeId,
}: React.PropsWithChildren<IBaseOrganizerItemProps>): JSX.Element {
  const organizersApi: InKindOrganizersAPI = new InKindOrganizersAPI();

  const [canEdit, setCanEdit] = React.useState<boolean>(organizer.canEdit);
  const [notifications, setNotifications] = React.useState<boolean>(organizer.notifications);
  const [isConfirmDialogOpen, setConfirmDialogOpen] = React.useState<boolean>(false);

  const onClickCanEdit = (canEdit: boolean, e: React.MouseEvent) => {
    if (!isOwner) {
      setCanEdit(canEdit);

      setOrganizerSettings(organizer.userId, {
        canEdit,
        notifications,
      });

      onUpdateSettings();
    }
  };

  const onClickNotifications = (notifications: boolean, e: React.MouseEvent) => {
    setNotifications(notifications);

    setOrganizerSettings(organizer.userId, {
      canEdit,
      notifications,
    });

    onUpdateSettings();
  };

  const onClickRemove = (e: React.MouseEvent) => {
    if (!isOwner) {
      e.preventDefault();
      setConfirmDialogOpen(true);
    }
  };

  const resendInvitation = async (invitation: OrganizerInvitation): Promise<void> => {
    await organizersApi.inviteUserAsPageOrganizer(routeId, invitation);
  };

  const setOrganizerSettings = async (userId: string, settings: InkindOrganizerSettings): Promise<void> => {
    if (isOwner) {
      await organizersApi.setOwnerSettings(routeId, settings);
    } else {
      await organizersApi.setOrganizerSettings(routeId, userId, settings);
    }
  };

  const removeOrganizer = async (userId: string): Promise<void> => {
    await organizersApi.removePageOrganizer(routeId, userId);
  };

  const cancelInvitation = async (emailAddress: string): Promise<void> => {
    await organizersApi.cancelOrganizerInvitation(routeId, emailAddress);
  };

  const onResendClick = function (e: React.MouseEvent, organizer: Organizer) {
    e.preventDefault();

    resendInvitation({
      emailAddress: organizer.email,
      canEdit: organizer.canEdit,
      notifications: organizer.notifications,
    });

    const notificationManager = window.notificationManager;
    notificationManager && notificationManager.ShowSuccessNotification('Email Resent Successfully.');
  };

  const confirmRemoveOrganizer = async function () {
    await removeOrganizer(organizer.userId);
    closeConfirmDialog();
    onRemoveOrganizer();
  };

  const closeConfirmDialog = function () {
    setConfirmDialogOpen(false);
  };

  return (
    <>
      <ConfirmDialog
        className="invite-section-confirm-dialog"
        title="Remove organizer"
        content={`Remove ${organizer.name} as a page organizer?`}
        onClickOk={() => {
          confirmRemoveOrganizer();
        }}
        onClickCancel={closeConfirmDialog}
        isOpen={isConfirmDialogOpen}
      />

      <section className={`organizer-entry ${className}`}>
        <div className="organizer-info">
          <div
            className="img-circle"
            style={{
              backgroundImage: `url('${
                isInvitee || !organizer.imgSrc ? getCDNAssetsUrl() + defaultUserImagePath : organizer.imgSrc
              }')`,
            }}
          />
          <div className="organizer-info-name">
            <div className="organizer-name">{isInvitee ? organizer.email : organizer.name}</div>
            {isOwner && <div className="owner-indicator">PAGE OWNER</div>}
            {isInvitee && <div className="owner-indicator">INVITED</div>}
          </div>
        </div>
        {!isInvitee ? (
          <>
            <div className="organizer-action-wrapper">
              <Checkbox disabled={isOwner} value={isOwner || canEdit} onChange={onClickCanEdit} />
            </div>
            <div className="organizer-action-wrapper">
              <Checkbox value={notifications} onChange={onClickNotifications} />
            </div>
            <div className="organizer-action-wrapper">
              <IconButton
                tooltip={isOwner ? 'You cannot delete the page owner.' : null}
                disabled={isOwner}
                icon="trash"
                style={IconButtonStyle.White}
                onClick={onClickRemove}
              />
            </div>
          </>
        ) : (
          <>
            <div className="organizer-action-wrapper">
              <Button style={ButtonStyle.Save} text="Resend" onClick={e => onResendClick(e, organizer)} />
            </div>
          </>
        )}
      </section>
    </>
  );
}

export interface IOrganizerListItemProps {
  className?: string;
  organizer: Organizer;
  onUpdateSettings: () => void;
  onRemoveOrganizer: () => void;
  routeId: string;
}

export function OwnerListItem(props: React.PropsWithChildren<IOrganizerListItemProps>): JSX.Element {
  return <BaseOrganizerItem isOwner={true} isInvitee={false} {...props} />;
}

export function OrganizerListItem(props: React.PropsWithChildren<IOrganizerListItemProps>): JSX.Element {
  return <BaseOrganizerItem isOwner={false} isInvitee={false} {...props} />;
}

export function InviteeListItem(props: React.PropsWithChildren<IOrganizerListItemProps>): JSX.Element {
  return <BaseOrganizerItem isOwner={false} isInvitee={true} {...props} />;
}
